import {
  ChangeDetectorRef,
  Component,
  AfterViewInit,
  NgZone,
  OnChanges,
  OnDestroy,
  input,
  output,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import moment from 'moment';
import { getAlterationForm } from 'src/app/forms/alterationForm';
import { IRouteAlteration } from 'src/app/models';
import { AlterationStatus, InputType, UserRole } from 'src/app/models/enums';
import { APP } from 'src/app/config/app';
import { LiteralService, StorageService, UnsavedChangesService, UtilsService } from 'src/app/services';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { images } from 'src/images';

@Component({
  selector: 'app-route-alteration-details',
  templateUrl: './route-alteration-details.component.html',
  styleUrl: './route-alteration-details.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RouteAlterationDetailsComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  readonly alterationDetail = input<IRouteAlteration>();
  readonly newAlteration = input<boolean>();
  readonly duplicateAlteration = input<boolean>();
  readonly edit = input<boolean>();
  readonly closed = input<boolean>();

  readonly editEvent = output();
  readonly deleteEvent = output();
  readonly action = output<IRouteAlteration | undefined>();
  readonly closeDetails = output<boolean | undefined>();
  readonly expandedChange = output<number>();

  private resizeObserver: ResizeObserver | null = null;

  public userRole: UserRole;
  public editStatus = false;
  public alterationForm: any[];
  public alterationStatus = AlterationStatus.ACTIVE;

  public images = images;
  public InputType = InputType;
  public AlterationStatus = AlterationStatus;
  public TooltipPosition = TooltipPosition;
  public UserRole = UserRole;

  public alterationStatusList = [
    { id: AlterationStatus.ACTIVE, name: AlterationStatus.ACTIVE },
    { id: AlterationStatus.INACTIVE, name: AlterationStatus.INACTIVE },
  ];

  constructor(
    public literalService: LiteralService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private storageService: StorageService,
    public utilsService: UtilsService,
    private unsavedChangesService: UnsavedChangesService,
  ) {}

  ngOnInit(): void {
    this.storageService.getRole().then((role) => {
      this.userRole = role;
    });
  }

  ngAfterViewInit(): void {
    const element = document.getElementById('alterationDetailsContainer');
    if (element) {
      this.ngZone.runOutsideAngular(() => {
        this.resizeObserver = new ResizeObserver((entries) => {
          const width = Math.max(Math.round(entries[0].contentRect.width), 32);
          this.ngZone.run(() => this.expandedChange.emit(width));
        });
        this.resizeObserver.observe(element);
      });
    }
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  async ngOnChanges(): Promise<void> {
    if (
      this.alterationDetail() ||
      this.newAlteration() ||
      this.duplicateAlteration() ||
      this.edit()
    ) {
      await this.updateAlterationForm();
      this.alterationStatus = this.alterationDetail()?.active
        ? AlterationStatus.ACTIVE
        : AlterationStatus.INACTIVE;
    }
    this.cdr.detectChanges();
  }

  public async updateAlterationForm(): Promise<void> {
    const isNew = this.isNewLikeAlteration();
    this.alterationForm = await getAlterationForm(
      this.alterationDetail()!,
      isNew,
      isNew ? false : this.edit()!,
      this.literalService,
    );
  }

  isNewLikeAlteration(): boolean {
    return !!this.newAlteration() || !!this.duplicateAlteration();
  }

  formTitle() {
    if (this.newAlteration()) return 'new';
    if (this.duplicateAlteration()) return 'duplicate.title';
    return 'title';
  }

  changeData(data: any, element?: any): void {
    const target = this.alterationDetail();
    let valueToChange = element?.edit?.valueToChange;
    if (!valueToChange || !target) return;
    let value =
      typeof data === 'object' ? data.target || data.options?.[0] : data;
    switch (valueToChange) {
      case 'startDate':
        valueToChange = 'startDateTime';
        const time = moment(target.startDateTime);
        value = moment(value.value, 'DD/MM/YYYY').toDate();
        if (time.isValid()) value.setHours(time.hours(), time.minutes());
        value = this.utilsService.toLocalDateTimeString(value);
        break;
      case 'startTime':
        valueToChange = 'startDateTime';
        const date = moment(target.startDateTime);
        value = moment(value.value, 'HH:mm').toDate();
        if (date.isValid()) value.setFullYear(date.year(), date.month(), date.date());
        value = this.utilsService.toLocalDateTimeString(value);
        break;
      case 'endDate':
        valueToChange = 'endDateTime';
        const endTime = moment(target.endDateTime);
        value = moment(value.value, 'DD/MM/YYYY').toDate();
        if (endTime.isValid()) value.setHours(endTime.hours(), endTime.minutes());
        value = this.utilsService.toLocalDateTimeString(value);
        break;
      case 'endTime':
        valueToChange = 'endDateTime';
        const endDateTime = moment(target.endDateTime);
        value = moment(value.value, 'HH:mm').toDate();
        if (endDateTime.isValid()) value.setFullYear(endDateTime.year(), endDateTime.month(), endDateTime.date());
        value = this.utilsService.toLocalDateTimeString(value);
        break;
      default:
        value = this.setNormalData(value, typeof data === 'object');
        break;
    }
    (target as any)[valueToChange] = value;
    this.updateAlterationForm();
  }

  private setNormalData(data: any, isEvent = false): any {
    return isEvent ? data?.value : data;
  }

  editStatusClick(): void {
    this.editStatus = !this.editStatus;
  }

  changeStatus(event: any): void {
    this.editStatus = false;
    if (event === null) return;
    const isActive = event === AlterationStatus.ACTIVE;
    this.alterationDetail()!['active'] = isActive;
    this.alterationStatus = event;
    this.updateAlterationForm();
    if (!this.isNewLikeAlteration()) this.action.emit(this.alterationDetail());
  }

  onExpand(): void {
    if (this.closed()) {
      this.closeDetails.emit(false);
    }
  }

  async onClose(): Promise<void> {
    if (this.isNewLikeAlteration()) {
      if (!(await this.unsavedChangesService.confirmDiscard(APP.ROUTE_DESIGN_UNSAVED_CHANGES_KEY))) return;
      this.alterationForm = [];
      this.editStatus = false;
      this.closeDetails.emit(undefined);
    } else {
      this.closeDetails.emit(true);
    }
  }

  isFormValid(): boolean {
    if (!this.alterationForm) return false;
    const target = this.alterationDetail();
    return this.alterationForm.every((element: any) => {
      if (!element.edit?.required) return true;
      let valueToChange = element.edit?.valueToChange;
      if (valueToChange === 'startDate' || valueToChange === 'startTime')
        valueToChange = 'startDateTime';
      if (valueToChange === 'endDate' || valueToChange === 'endTime')
        valueToChange = 'endDateTime';
      const value = (target as any)?.[valueToChange];
      return (
        value !== null && value !== undefined && value !== '' && value !== '-'
      );
    });
  }

  onEdit() {
    this.editEvent.emit();
  }

  onDelete() {
    this.deleteEvent.emit();
  }
}
